/**
 * Live scanning: what the lens sees, and when a look becomes a capture.
 *
 * The scanner ManaBox has is one continuous session - the viewfinder never closes,
 * and a card that is framed and still is taken without a press. That behaviour is
 * a policy, not a camera feature, so it lives here: no DOM, no `getUserMedia`, no
 * bridge, and every timing decision in one place that a test can drive frame by
 * frame.
 *
 * Two things are deliberately *not* decided here:
 *
 *  - **whether a card is in the picture.** That is the shared detector's call, and
 *    on a phone the native analysis loop only reports physics (`luma`, `edge`,
 *    `motion`). This module is told an observation; it never looks at pixels.
 *  - **what the card is.** That is `readCapture` and the cascade, downstream.
 *
 * The one rule worth stating twice is the re-arm: after a capture the scanner does
 * *not* fire again while the same card sits in front of it. It waits for the card to
 * leave (or for a long timeout), because a scanner that re-fires on a card being
 * read files it twice - and the duplicate is only discovered after two copies exist.
 * A deliberate tap is the way to take the same card again, which is exactly what
 * ManaBox's "tap to scan the same card consecutively" is for.
 */

import { quadDistance, type Quad } from '@unsorted/shared';

/** What one look at the frame says, before any timing is applied. */
export interface Observation {
  /**
   * How the card sits in the guide:
   *  - `none`   - nothing that looks like a card is in the frame;
   *  - `small`  - a card is there, but too far away to read;
   *  - `skewed` - a card is there, at an angle the detector will not straighten;
   *  - `framed` - a card fills the guide.
   */
  card: 'none' | 'small' | 'skewed' | 'framed';
  /** False while the card (or the hand) is still moving between frames. */
  steady: boolean;
  /**
   * The numbers behind the observation, for the scanner's diagnostics line. Never
   * used to decide anything: a scanner that explains itself can be tuned on a phone
   * instead of by guessing at a rebuild.
   */
  detail?: string;
}

export type LiveState = 'empty' | 'too-far' | 'skewed' | 'moving' | 'framed' | 'waiting';

export interface LiveDecision {
  /** Take a frame now. True at most once per settled card. */
  capture: boolean;
  state: LiveState;
  /** One sentence for the operator, shown verbatim under the guide. */
  hint: string;
  /** How far into the settle the card is, 0..1, for the guide's progress ring. */
  progress: number;
  /** The numbers the observation was made from, for the diagnostics line. */
  detail?: string;
}

export interface LiveSessionOptions {
  /** Consecutive framed-and-steady looks before the shutter fires. */
  settleFrames?: number;
  /** Consecutive empty looks before the scanner is ready for the next card. */
  rearmFrames?: number;
  /** Milliseconds ignored after a *filed* capture, on top of the re-arm. */
  cooldownMs?: number;
  /** Milliseconds ignored after a capture that held no card at all. */
  refusedCooldownMs?: number;
  /**
   * How long a card may stay in frame before the scanner gives up waiting for it to
   * leave. Five seconds is longer than any read takes; hitting it means the user is
   * holding a card still, and the second capture is the price of not stalling.
   */
  maxWaitMs?: number;
  /** Rejected captures before the surface is blamed instead of the aim. */
  backgroundHintAfter?: number;
}

/** The sentence the sheet shows for each state. */
export const LIVE_HINTS: Readonly<Record<LiveState, string>> = {
  empty: 'Point at a card',
  'too-far': 'Move closer',
  skewed: 'Keep the card flat to the lens',
  moving: 'Hold still',
  framed: 'Hold it there…',
  waiting: 'Reading…',
};

/** After a capture that found no card, the surface is the likelier problem. */
export const BACKGROUND_HINT = 'Point at a card on a plain, contrasting surface — glare and shadows hide the edges.';

/** How far a card may move between looks and still count as settled, as a share of the frame. */
export const STEADY_SHARE = 0.02;

/** The smallest share of the frame's width a card may take and still be read. */
export const MIN_COVERAGE = 0.3;

/**
 * The card's mean width over its mean height, for the quad the detector found.
 *
 * The printed ratio is 63/88 (0.716). A quad whose sides disagree with that by more
 * than a few percent was photographed at an angle steep enough that the rectified
 * card is not the shape the art window was measured on (ADR 0008), and the honest
 * answer is to ask for a flatter card rather than to guess.
 */
export function quadAspect(quad: Quad): number {
  const [topLeft, topRight, bottomRight, bottomLeft] = quad;
  const width = (Math.hypot(topRight.x - topLeft.x, topRight.y - topLeft.y) +
    Math.hypot(bottomRight.x - bottomLeft.x, bottomRight.y - bottomLeft.y)) / 2;
  const height = (Math.hypot(bottomLeft.x - topLeft.x, bottomLeft.y - topLeft.y) +
    Math.hypot(bottomRight.x - topRight.x, bottomRight.y - topRight.y)) / 2;
  return height === 0 ? 0 : width / height;
}

/** The aspect band a framed card may sit in before the surface asks for a flatter one. */
export const ASPECT_BAND = { min: 0.55, max: 0.92 } as const;

export interface ObserveOptions {
  minCoverage?: number;
  steadyShare?: number;
  aspectBand?: { min: number; max: number };
}

/**
 * One frame's detector result, as an observation.
 *
 * This runs the *shared* detector's answer through the two judgements a live loop
 * needs and nothing more: is the card big enough, is it flat enough, and did it move
 * since the last look. The quad itself, the art window and the fingerprint stay
 * downstream where they are measured.
 */
export function observationFromQuad(
  quad: Quad | null,
  coverage: number,
  previous: Quad | null,
  frameWidth: number,
  options: ObserveOptions = {},
): Observation {
  const minCoverage = options.minCoverage ?? MIN_COVERAGE;
  const steadyShare = options.steadyShare ?? STEADY_SHARE;
  const band = options.aspectBand ?? ASPECT_BAND;

  if (quad === null) return { card: 'none', steady: false };

  const aspect = quadAspect(quad);
  const moved = previous === null ? Number.POSITIVE_INFINITY : quadDistance(quad, previous);
  const detail = `card ${(coverage * 100).toFixed(0)}% of the frame · ratio ${aspect.toFixed(2)} · moved ${moved === Number.POSITIVE_INFINITY ? '—' : moved.toFixed(0)}px`;

  if (coverage < minCoverage) return { card: 'small', steady: false, detail };
  if (aspect < band.min || aspect > band.max) return { card: 'skewed', steady: false, detail };

  const steady = previous !== null && moved <= frameWidth * steadyShare;
  return { card: 'framed', steady, detail };
}

/**
 * The policy: one card, one capture, then wait for the next one.
 *
 * Every method takes the clock rather than reading it, so a test can hold a card
 * still for exactly three frames without sleeping.
 */
export class LiveSession {
  private readonly settleFrames: number;
  private readonly rearmFrames: number;
  private readonly cooldownMs: number;
  private readonly refusedCooldownMs: number;
  private readonly maxWaitMs: number;
  private readonly backgroundHintAfter: number;

  private phase: 'armed' | 'waiting' = 'armed';
  private settled = 0;
  private empty = 0;
  private lastFireAt = Number.NEGATIVE_INFINITY;
  private cooldown = 0;
  private refusals = 0;

  constructor(options: LiveSessionOptions = {}) {
    this.settleFrames = options.settleFrames ?? 3;
    this.rearmFrames = options.rearmFrames ?? 2;
    this.cooldownMs = options.cooldownMs ?? 900;
    this.refusedCooldownMs = options.refusedCooldownMs ?? 600;
    this.maxWaitMs = options.maxWaitMs ?? 5000;
    this.backgroundHintAfter = options.backgroundHintAfter ?? 2;
  }

  /** A look at the frame. Returns what the sheet should show, and whether to fire. */
  observe(observation: Observation, now: number): LiveDecision {
    const detail = observation.detail === undefined ? {} : { detail: observation.detail };
    const decision = (capture: boolean, state: LiveState, hint: string, progress: number): LiveDecision => ({
      capture,
      state,
      hint,
      progress,
      ...detail,
    });

    if (this.phase === 'waiting') {
      if (observation.card === 'none') this.empty += 1;
      else this.empty = 0;

      const waited = now - this.lastFireAt;
      const cardLeft = this.empty >= this.rearmFrames && waited >= this.cooldown;
      const heldTooLong = waited >= this.maxWaitMs;
      if (!cardLeft && !heldTooLong) {
        return decision(false, 'waiting', this.waitingHint(), 0);
      }
      this.phase = 'armed';
      this.settled = 0;
      this.empty = 0;
    }

    if (observation.card === 'framed' && observation.steady) {
      this.settled += 1;
      if (this.settled >= this.settleFrames) {
        this.fire(now);
        return decision(true, 'framed', 'Got it', 1);
      }
      return decision(false, 'framed', LIVE_HINTS.framed, this.settled / this.settleFrames);
    }

    this.settled = 0;
    return decision(false, stateOf(observation), this.hintFor(observation), 0);
  }

  /**
   * A deliberate tap: take the frame now, without waiting for three steady looks.
   *
   * Still subject to the cooldown, because a tap during a read is a user trying to
   * hurry the scanner, not a second card.
   */
  request(now: number): LiveDecision {
    if (this.phase === 'waiting' && now - this.lastFireAt < this.cooldown) {
      return { capture: false, state: 'waiting', hint: this.waitingHint(), progress: 0 };
    }
    this.fire(now);
    return { capture: true, state: 'framed', hint: 'Got it', progress: 1 };
  }

  /** The capture came back with no card: a framing failure, not a filed card. */
  refused(now: number): void {
    this.refusals += 1;
    this.lastFireAt = now;
    this.cooldown = this.refusedCooldownMs;
    this.phase = 'waiting';
    this.settled = 0;
    this.empty = 0;
  }

  /** A capture the surface accepted: wait for the card to leave before firing again. */
  accepted(now: number): void {
    this.refusals = 0;
    this.lastFireAt = now;
    this.cooldown = this.cooldownMs;
    this.phase = 'waiting';
    this.settled = 0;
    this.empty = 0;
  }

  /** Close, reopen, or change the aim: back to looking for a card. */
  reset(): void {
    this.phase = 'armed';
    this.settled = 0;
    this.empty = 0;
    this.lastFireAt = Number.NEGATIVE_INFINITY;
    this.refusals = 0;
  }

  private fire(now: number): void {
    this.lastFireAt = now;
    this.cooldown = this.cooldownMs;
    this.phase = 'waiting';
    this.settled = 0;
    this.empty = 0;
  }

  private waitingHint(): string {
    return this.refusals >= this.backgroundHintAfter ? BACKGROUND_HINT : LIVE_HINTS.waiting;
  }

  private hintFor(observation: Observation): string {
    const state = stateOf(observation);
    if (state === 'empty' && this.refusals >= this.backgroundHintAfter) return BACKGROUND_HINT;
    return LIVE_HINTS[state];
  }
}

const stateOf = (observation: Observation): LiveState => {
  if (observation.card === 'none') return 'empty';
  if (observation.card === 'small') return 'too-far';
  if (observation.card === 'skewed') return 'skewed';
  return observation.steady ? 'framed' : 'moving';
};
